/**
 * Games Screen - Overview of all family games
 * Redesigned with Weekend Competition and Free Play sections
 */

import { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
} from "react-native";
import { supabase } from "@/lib/supabase/client";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { format, formatDistanceToNow, differenceInHours, differenceInMinutes } from "date-fns";
import { nl } from "date-fns/locale";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import {
  useUserChallenges,
  ChallengeWithStatus,
  CHALLENGE_ICONS,
  CHALLENGE_COLORS,
} from "@/modules/challenges";
import {
  useGameRegistry,
  useGameStats,
  GameWithConfig,
  Leaderboard,
} from "@/modules/games";
import { useSpeurtochts } from "@/modules/speurtocht";

/**
 * Waiting game interface - covers both sent and received challenges
 */
interface WaitingGame {
  id: string;
  game_slug: string;
  other_player_name: string;
  created_at: string;
  mode: "free" | "weekend";
  /** Whether current user is the one who sent the challenge */
  isSentByMe: boolean;
}

/**
 * Games Screen Component
 */
export default function GamesScreen() {
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { user, userTeam, activeWeekend } = useAuth();

  // Waiting games state (both sent and received challenges)
  const [waitingGames, setWaitingGames] = useState<WaitingGame[]>([]);

  // Challenges hook
  const {
    activeChallenges,
    pendingChallenges,
    urgentChallenges,
    newChallenges,
    completedChallenges,
    totalAvailablePoints,
    earnedPoints,
    isLoading: isLoadingChallenges,
    refetch: refetchChallenges,
  } = useUserChallenges();

  // Games framework hooks
  const {
    weekendGames,
    freePlayGames,
    isCompetitionActive,
    competitionSettings,
    isLoading: isLoadingGames,
    refetch: refetchGames,
  } = useGameRegistry();

  const {
    globalLeaderboard,
    myGlobalRank,
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useGameStats({ leaderboardLimit: 5 });

  // Speurtocht hook to get dynamic points
  const {
    speurtochts,
    isLoading: isLoadingSpeurtochts,
    refetch: refetchSpeurtochts,
  } = useSpeurtochts();

  // Calculate total available speurtocht points
  const activeSpeurtocht = speurtochts.find((s) => s.status === "active");
  const speurtochtPointsText = activeSpeurtocht
    ? `+${activeSpeurtocht.points_per_step} pts/stap`
    : "Binnenkort";

  // Use activeProfile for game queries (supports guardian mode)
  const { activeProfile } = useAuth();
  const targetUserId = activeProfile?.id;

  /**
   * Fetch waiting games - includes both sent and received challenges
   * Uses activeProfile to support guardian mode
   */
  const fetchWaitingGames = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error } = await supabase
        .from("game_sessions")
        .select("id, game_slug, created_at, mode, game_state, created_by")
        .eq("status", "waiting")
        .eq("session_type", "versus")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter to show games where active profile is either challenger or challenged
      const games: WaitingGame[] = (data || [])
        .filter((session) => {
          const gameState = session.game_state as Record<string, unknown> | null;
          const isChallengedUser = gameState?.challenged_user_id === targetUserId;
          const isChallenger = session.created_by === targetUserId;
          return isChallengedUser || isChallenger;
        })
        .map((session) => {
          const gameState = session.game_state as Record<string, unknown>;
          const isChallenger = session.created_by === targetUserId;
          
          return {
            id: session.id,
            game_slug: session.game_slug,
            // Show the other person's name
            other_player_name: isChallenger
              ? (gameState?.challenged_user_name as string) || "Tegenstander"
              : (gameState?.challenger_name as string) || "Iemand",
            created_at: session.created_at,
            mode: session.mode as "free" | "weekend",
            isSentByMe: isChallenger,
          };
        });

      setWaitingGames(games);
    } catch (err) {
      console.error("Error fetching waiting games:", err);
    }
  }, [targetUserId]);

  // Fetch waiting games on mount and subscribe to updates
  useEffect(() => {
    fetchWaitingGames();

    if (!targetUserId) return;

    // Subscribe to game session updates
    const channel = supabase
      .channel(`waiting_games_banner_${targetUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
          filter: `status=eq.waiting`,
        },
        () => {
          fetchWaitingGames();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [targetUserId, fetchWaitingGames]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([
      refetchChallenges(),
      refetchGames(),
      refetchStats(),
      refetchSpeurtochts(),
      fetchWaitingGames(),
    ]);
    setIsRefreshing(false);
  }, [refetchChallenges, refetchGames, refetchStats, refetchSpeurtochts, fetchWaitingGames]);

  /**
   * Navigate to challenge detail
   */
  const handleChallengePress = (challenge: ChallengeWithStatus) => {
    // Route opdrachten to the opdracht screen
    if (challenge.challenge_type === "opdracht") {
      router.push(`/(tabs)/games/opdracht/${challenge.id}`);
    } else {
      router.push(`/(tabs)/games/challenge/${challenge.id}`);
    }
  };

  /**
   * Navigate to game based on slug
   * Maps database slugs to actual route paths
   * Passes mode as query param to auto-select game mode
   */
  const handleGamePress = (game: GameWithConfig, mode: "weekend" | "free") => {
    // Map database slugs to route paths
    const routeMap: Record<string, string> = {
      tic_tac_toe: "tic-tac-toe",
      puzzle_matching: "memory",
      chess: "chess",
      sudoku: "sudoku",
      match_3: "match3",
      endless_runner: "endless-runner",
      dammen: "dammen",
    };

    const routePath = routeMap[game.slug] || game.slug;
    
    // Navigate using consistent /(tabs)/games/ path format with mode query param
    router.push(`/(tabs)/games/${routePath}?mode=${mode}` as any);
  };

  /**
   * Format deadline text
   */
  const formatDeadline = (dateStr: string) => {
    const date = new Date(dateStr);
    const hours = differenceInHours(date, new Date());
    const minutes = differenceInMinutes(date, new Date()) % 60;

    if (hours < 1) {
      return `${minutes} min`;
    } else if (hours < 24) {
      return `${hours}u ${minutes}m`;
    }
    return formatDistanceToNow(date, { locale: nl });
  };

  /**
   * Format competition end date
   */
  const getCompetitionEndText = () => {
    if (!competitionSettings?.global_ends_at) return null;
    const endDate = new Date(competitionSettings.global_ends_at);
    return format(endDate, "d MMMM HH:mm", { locale: nl });
  };

  const isLoading = isLoadingChallenges || isLoadingGames;

  return (
    <SafeAreaView className="flex-1" edges={["top", "left", "right"]} style={{ backgroundColor: colors.background }}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            Familie Spellen
          </Text>
          <View className="flex-row items-center gap-2">
            {/* Mijn Spellen Button */}
            <TouchableOpacity
              onPress={() => router.push("/(tabs)/games/active-games" as any)}
              className="flex-row items-center px-3 py-1.5 rounded-full"
              style={{ backgroundColor: colors.primaryLight }}
              accessibilityLabel="Mijn Spellen"
              accessibilityRole="button"
            >
              <Ionicons name="game-controller" size={14} color={colors.primary} />
              <Text className="ml-1 text-sm font-semibold" style={{ color: colors.primary }}>
                Mijn Spellen
              </Text>
            </TouchableOpacity>
            {/* Rank Badge */}
            {myGlobalRank && (
              <TouchableOpacity
                onPress={() => router.push("/scores")}
                className="flex-row items-center px-3 py-1.5 rounded-full"
                style={{ backgroundColor: "#F59E0B20" }}
              >
                <Ionicons name="trophy" size={14} color="#F59E0B" />
                <Text className="ml-1 text-sm font-semibold" style={{ color: "#F59E0B" }}>
                  #{myGlobalRank}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Active Games Card - Consolidated view linking to dedicated screen */}
        {waitingGames.length > 0 && (
          <TouchableOpacity
            className="mx-4 mb-4 rounded-2xl p-4 flex-row items-center"
            style={{ 
              backgroundColor: waitingGames.some(g => !g.isSentByMe) ? "#F59E0B" : "#8B5CF6"
            }}
            onPress={() => router.push("/(tabs)/games/active-games" as any)}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={`${waitingGames.length} actieve spellen bekijken`}
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-4"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <Ionicons 
                name={waitingGames.some(g => !g.isSentByMe) ? "game-controller" : "hourglass-outline"} 
                size={24} 
                color="#FFFFFF" 
              />
            </View>
            <View className="flex-1">
              <Text className="font-bold text-white text-lg">
                {waitingGames.some(g => !g.isSentByMe) 
                  ? `🎮 ${waitingGames.filter(g => !g.isSentByMe).length} ${waitingGames.filter(g => !g.isSentByMe).length === 1 ? "uitnodiging" : "uitnodigingen"}`
                  : `⏳ ${waitingGames.length} wachtend${waitingGames.length === 1 ? "" : "e"}`
                }
              </Text>
              <Text className="text-white/90">
                {waitingGames.some(g => !g.isSentByMe)
                  ? "Je hebt uitdagingen ontvangen!"
                  : "Wachten op reactie van anderen"}
              </Text>
            </View>
            <View
              className="px-3 py-2 rounded-lg flex-row items-center"
              style={{ backgroundColor: "rgba(255,255,255,0.3)" }}
            >
              <Text className="text-white font-semibold mr-1">Bekijk</Text>
              <Ionicons name="chevron-forward" size={16} color="#FFFFFF" />
            </View>
          </TouchableOpacity>
        )}

        {/* Weekend Competition Banner */}
        {isCompetitionActive && activeWeekend && (
          <View className="mx-4 mb-4">
            <View
              className="rounded-2xl p-4 overflow-hidden"
              style={{
                backgroundColor: "#8B5CF6",
              }}
            >
              <View className="flex-row items-center justify-between">
                <View className="flex-1">
                  <View className="flex-row items-center">
                    <Ionicons name="trophy" size={20} color="#FFFFFF" />
                    <Text className="ml-2 font-bold text-lg text-white">
                      Weekend Competitie
                    </Text>
                  </View>
                  <Text className="text-sm text-white/80 mt-1">
                    {activeWeekend.name}
                  </Text>
                  {getCompetitionEndText() && (
                    <Text className="text-xs text-white/60 mt-1">
                      Tot {getCompetitionEndText()}
                    </Text>
                  )}
                </View>
                {userTeam && (
                  <TouchableOpacity
                    onPress={() => router.push(`/scores/team/${userTeam.id}`)}
                    className="items-center px-4 py-2 rounded-xl"
                    style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  >
                    <Text className="text-2xl font-bold text-white">
                      {userTeam.total_score}
                    </Text>
                    <Text className="text-xs text-white/80">
                      {userTeam.name}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        )}

        {/* ===== FAMILIE WEEKEND SPELLEN SECTION ===== */}
        {isCompetitionActive && activeWeekend && (
          <View
            className="mb-6 mx-4 rounded-2xl overflow-hidden"
            style={{
              backgroundColor: "#8B5CF608",
              borderWidth: 2,
              borderColor: "#8B5CF640",
            }}
          >
            {/* Section Header Banner */}
            <View
              className="px-4 py-3"
              style={{
                backgroundColor: "#8B5CF6",
              }}
            >
              <View className="flex-row items-center justify-between">
                <View className="flex-row items-center">
                  <View
                    className="w-8 h-8 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  >
                    <Ionicons name="trophy" size={16} color="#FFFFFF" />
                  </View>
                  <View>
                    <Text className="font-bold text-lg text-white">
                      Familie Weekend
                    </Text>
                    <Text className="text-xs text-white/70">
                      Verdien punten voor je team!
                    </Text>
                  </View>
                </View>
                {userTeam && (
                  <View className="items-end">
                    <Text className="text-xl font-bold text-white">
                      {userTeam.total_score}
                    </Text>
                    <Text className="text-xs text-white/70">punten</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Section Content */}
            <View className="py-4">

            {/* 1. Spellen Section - Future mini-games (excludes quiz/speurtocht) */}
            <View className="mb-4">
              <View className="px-4 flex-row items-center mb-3">
                <Ionicons name="game-controller" size={16} color="#8B5CF6" />
                <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                  Spellen
                </Text>
                {weekendGames.filter((g) => g.slug !== "quiz" && g.slug !== "speurtocht").length > 0 && (
                  <View
                    className="ml-2 px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "#8B5CF620" }}
                  >
                    <Text className="text-xs font-medium" style={{ color: "#8B5CF6" }}>
                      +punten
                    </Text>
                  </View>
                )}
              </View>

              {weekendGames.filter((g) => g.slug !== "quiz" && g.slug !== "speurtocht").length === 0 ? (
                <View
                  className="mx-4 rounded-xl p-4 items-center"
                  style={{
                    backgroundColor: "#8B5CF608",
                    borderWidth: 1,
                    borderColor: "#8B5CF620",
                    borderStyle: "dashed",
                  }}
                >
                  <Ionicons name="game-controller-outline" size={24} color="#8B5CF650" />
                  <Text
                    className="text-sm mt-2 text-center"
                    style={{ color: colors.textSecondary }}
                  >
                    Binnenkort beschikbaar
                  </Text>
                </View>
              ) : (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  className="pl-4"
                >
                  {weekendGames
                    .filter((g) => g.slug !== "quiz" && g.slug !== "speurtocht")
                    .map((game) => (
                      <WeekendGameCard
                        key={game.slug}
                        game={game}
                        colors={colors}
                        onPress={() => handleGamePress(game, "weekend")}
                      />
                    ))}
                  <View className="w-4" />
                </ScrollView>
              )}
            </View>

            {/* 2. Quiz & Speurtocht Section */}
            <View className="mb-4 px-4">
              <View className="flex-row items-center mb-3">
                <Ionicons name="search" size={16} color="#F97316" />
                <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                  Quiz & Speurtocht
                </Text>
              </View>

              <View className="flex-row">
                {/* Quiz Card */}
                <TouchableOpacity
                  onPress={() => router.push("/(tabs)/games/quiz?mode=weekend" as any)}
                  activeOpacity={0.7}
                  className="flex-1 rounded-2xl p-4 mr-2"
                  style={{
                    backgroundColor: "#6366F115",
                    borderWidth: 1,
                    borderColor: "#6366F140",
                  }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                    style={{ backgroundColor: "#6366F130" }}
                  >
                    <Ionicons name="help-circle" size={22} color="#6366F1" />
                  </View>
                  <Text className="font-semibold" style={{ color: colors.text }}>
                    Familie Quiz
                  </Text>
                  <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                    Test je kennis
                  </Text>
                  <View className="flex-row items-center mt-3">
                    <Ionicons name="star" size={12} color="#F59E0B" />
                    <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
                      +10 pts
                    </Text>
                  </View>
                </TouchableOpacity>

                {/* Speurtocht Card */}
                <TouchableOpacity
                  onPress={() => router.push("/(tabs)/games/speurtocht" as any)}
                  activeOpacity={0.7}
                  className="flex-1 rounded-2xl p-4 ml-2"
                  style={{
                    backgroundColor: "#F9731615",
                    borderWidth: 1,
                    borderColor: "#F9731640",
                  }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                    style={{ backgroundColor: "#F9731630" }}
                  >
                    <Ionicons name="map" size={22} color="#F97316" />
                  </View>
                  <Text className="font-semibold" style={{ color: colors.text }}>
                    Speurtocht
                  </Text>
                  <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                    Ontdek locaties
                  </Text>
                  <View className="flex-row items-center mt-3">
                    <Ionicons name="star" size={12} color="#F59E0B" />
                    <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
                      {speurtochtPointsText}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>

            {/* 3. Challenges Section */}
            <View className="px-4">
              <View className="flex-row items-center justify-between mb-3">
                <View className="flex-row items-center">
                  <Ionicons name="flash" size={16} color="#EA580C" />
                  <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                    Uitdagingen
                  </Text>
                  {activeChallenges.length > 0 && (
                    <View
                      className="ml-2 min-w-[22px] h-[22px] rounded-full items-center justify-center px-1.5"
                      style={{ backgroundColor: "#EA580C" }}
                    >
                      <Text className="text-xs font-bold text-white">{activeChallenges.length}</Text>
                    </View>
                  )}
                </View>
                {activeChallenges.length > 0 && (
                  <Text className="text-xs" style={{ color: "#F59E0B" }}>
                    {totalAvailablePoints} pts beschikbaar
                  </Text>
                )}
              </View>

              {activeChallenges.length === 0 ? (
                <View
                  className="rounded-xl p-4 items-center"
                  style={{
                    backgroundColor: "#EA580C08",
                    borderWidth: 1,
                    borderColor: "#EA580C20",
                    borderStyle: "dashed",
                  }}
                >
                  <Ionicons name="flash-outline" size={24} color="#EA580C50" />
                  <Text
                    className="text-sm mt-2 text-center"
                    style={{ color: colors.textSecondary }}
                  >
                    Nog geen uitdagingen beschikbaar
                  </Text>
                </View>
              ) : (
                <>
                  {activeChallenges.slice(0, 2).map((challenge) => (
                    <TouchableOpacity
                      key={challenge.id}
                      onPress={() => handleChallengePress(challenge)}
                      activeOpacity={0.7}
                      className="rounded-xl p-3 mb-2 flex-row items-center"
                      style={{
                        backgroundColor: "#EA580C10",
                        borderWidth: 1,
                        borderColor: "#EA580C30",
                      }}
                    >
                      <View
                        className="w-10 h-10 rounded-lg items-center justify-center"
                        style={{
                          backgroundColor: `${CHALLENGE_COLORS[challenge.challenge_type] || "#EA580C"}20`,
                        }}
                      >
                        <Ionicons
                          name={
                            (CHALLENGE_ICONS[challenge.challenge_type] || "flash") as any
                          }
                          size={18}
                          color={CHALLENGE_COLORS[challenge.challenge_type] || "#EA580C"}
                        />
                      </View>
                      <View className="flex-1 ml-3">
                        <Text className="font-medium" style={{ color: colors.text }}>
                          {challenge.title}
                        </Text>
                        <View className="flex-row items-center mt-1">
                          <Ionicons name="star" size={12} color="#F59E0B" />
                          <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                            {challenge.points} punten
                          </Text>
                        </View>
                      </View>
                      <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
                    </TouchableOpacity>
                  ))}

                  {activeChallenges.length > 2 && (
                    <TouchableOpacity className="py-2 items-center">
                      <Text className="text-sm font-medium" style={{ color: "#EA580C" }}>
                        Bekijk alle {activeChallenges.length} uitdagingen
                      </Text>
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>
            </View>
          </View>
        )}

        {/* ===== VRIJ SPELEN SECTION ===== */}
        <View
          className="mb-6 mx-4 rounded-2xl overflow-hidden"
          style={{
            backgroundColor: isDark ? "#1F2937" : "#F8FAFC",
            borderWidth: 2,
            borderColor: isDark ? "#374151" : "#E2E8F0",
          }}
        >
          {/* Section Header Banner */}
          <View
            className="px-4 py-3"
            style={{
              backgroundColor: isDark ? "#374151" : "#E2E8F0",
            }}
          >
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center">
                <View
                  className="w-8 h-8 rounded-lg items-center justify-center mr-3"
                  style={{ backgroundColor: isDark ? "#4B5563" : "#CBD5E1" }}
                >
                  <Ionicons name="game-controller" size={16} color={isDark ? "#9CA3AF" : "#64748B"} />
                </View>
                <View>
                  <Text
                    className="font-bold text-lg"
                    style={{ color: isDark ? "#F3F4F6" : "#334155" }}
                  >
                    Staats Spellen
                  </Text>
                  <Text
                    className="text-xs"
                    style={{ color: isDark ? "#9CA3AF" : "#64748B" }}
                  >
                    Vrije of weekendmodus voor punten
                  </Text>
                </View>
              </View>
              <View
                className="px-2 py-1 rounded-full"
                style={{ backgroundColor: isDark ? "#4B5563" : "#CBD5E1" }}
              >
                <Text
                  className="text-xs font-medium"
                  style={{ color: isDark ? "#D1D5DB" : "#475569" }}
                >
                  veel plezier
                </Text>
              </View>
            </View>
          </View>

          {/* Section Content */}
          <View className="p-4">
            {/* Free Play Games */}
            <View className="flex-row flex-wrap">
              {/* Quiz - Weekend Only (Locked) */}
              <View
                className="w-[48%] rounded-2xl p-4 mb-3 mr-[4%] opacity-60"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <View className="flex-row items-start justify-between">
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                    style={{ backgroundColor: "#6366F120" }}
                  >
                    <Ionicons name="help-circle" size={20} color="#6366F1" />
                  </View>
                  <View
                    className="px-2 py-1 rounded-full"
                    style={{ backgroundColor: "#F59E0B20" }}
                  >
                    <Ionicons name="lock-closed" size={12} color="#F59E0B" />
                  </View>
                </View>
                <Text className="font-semibold" style={{ color: colors.text }}>
                  Quiz
                </Text>
                <Text className="text-xs mt-1" style={{ color: "#F59E0B" }}>
                  Alleen tijdens familieweekend
                </Text>
              </View>

              {/* Speurtocht - Weekend Only (Locked) */}
              <View
                className="w-[48%] rounded-2xl p-4 mb-3 opacity-60"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <View className="flex-row items-start justify-between">
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                    style={{ backgroundColor: "#F9731620" }}
                  >
                    <Ionicons name="map" size={20} color="#F97316" />
                  </View>
                  <View
                    className="px-2 py-1 rounded-full"
                    style={{ backgroundColor: "#F59E0B20" }}
                  >
                    <Ionicons name="lock-closed" size={12} color="#F59E0B" />
                  </View>
                </View>
                <Text className="font-semibold" style={{ color: colors.text }}>
                  Speurtocht
                </Text>
                <Text className="text-xs mt-1" style={{ color: "#F59E0B" }}>
                  Alleen tijdens familieweekend
                </Text>
              </View>

              {/* Additional free play games from registry */}
              {freePlayGames
                .filter((g) => g.slug !== "quiz" && g.slug !== "speurtocht")
                .map((game, index) => (
                  <FreePlayGameCard
                    key={game.slug}
                    game={game}
                    colors={colors}
                    onPress={() => handleGamePress(game, "free")}
                    isLeftColumn={index % 2 === 0}
                  />
                ))}
            </View>
          </View>
        </View>

        {/* Urgent Challenges */}
        {urgentChallenges.length > 0 && (
          <View className="mb-6">
            <View className="px-4 flex-row items-center mb-3">
              <Ionicons name="time" size={18} color={colors.error} />
              <Text className="font-bold text-lg ml-2" style={{ color: colors.text }}>
                Bijna Verlopen
              </Text>
              <View
                className="ml-2 px-2 py-0.5 rounded-full"
                style={{ backgroundColor: colors.error }}
              >
                <Text className="text-xs font-bold text-white">
                  {urgentChallenges.length}
                </Text>
              </View>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              className="pl-4"
            >
              {urgentChallenges.map((challenge) => (
                <UrgentChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  colors={colors}
                  onPress={() => handleChallengePress(challenge)}
                  formatDeadline={formatDeadline}
                />
              ))}
              <View className="w-4" />
            </ScrollView>
          </View>
        )}

        {/* Active Challenges Section (when no competition) */}
        {activeChallenges.length > 0 && !isCompetitionActive && (
          <View className="mb-6 px-4">
            <View className="flex-row items-center justify-between mb-3">
              <View className="flex-row items-center">
                <Ionicons name="flash" size={18} color="#EA580C" />
                <Text className="font-bold text-lg ml-2" style={{ color: colors.text }}>
                  Actieve Uitdagingen
                </Text>
              </View>
              <Text style={{ color: colors.textSecondary }}>
                {activeChallenges.length} beschikbaar
              </Text>
            </View>

            {activeChallenges.slice(0, 3).map((challenge) => (
              <ChallengeListCard
                key={challenge.id}
                challenge={challenge}
                colors={colors}
                isDark={isDark}
                onPress={() => handleChallengePress(challenge)}
              />
            ))}

            {activeChallenges.length > 3 && (
              <TouchableOpacity className="py-3 items-center">
                <Text style={{ color: colors.primary }}>
                  Bekijk alle {activeChallenges.length} uitdagingen
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Pending Challenges */}
        {pendingChallenges.length > 0 && (
          <View className="mb-6 px-4">
            <View className="flex-row items-center mb-3">
              <Ionicons name="hourglass" size={18} color="#F59E0B" />
              <Text className="font-bold text-lg ml-2" style={{ color: colors.text }}>
                In Afwachting
              </Text>
              <View
                className="ml-2 px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#F59E0B" }}
              >
                <Text className="text-xs font-bold text-white">{pendingChallenges.length}</Text>
              </View>
            </View>
            {pendingChallenges.slice(0, 2).map((challenge) => (
              <TouchableOpacity
                key={challenge.id}
                onPress={() => handleChallengePress(challenge)}
                activeOpacity={0.7}
                className="rounded-xl p-3 mb-2 flex-row items-center"
                style={{
                  backgroundColor: "#F59E0B10",
                  borderWidth: 1,
                  borderColor: "#F59E0B30",
                }}
              >
                <View
                  className="w-10 h-10 rounded-lg items-center justify-center"
                  style={{ backgroundColor: "#F59E0B20" }}
                >
                  <Ionicons name="hourglass" size={18} color="#F59E0B" />
                </View>
                <View className="flex-1 ml-3">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {challenge.title}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Wacht op beoordeling
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Free Play Leaderboard */}
        {competitionSettings?.show_leaderboard && globalLeaderboard.length > 0 && (
          <View className="mb-6 px-4">
            <Leaderboard
              entries={globalLeaderboard}
              title="Vrije Spellen Totale Ranglijst"
              maxEntries={5}
              showSeeAll
              onSeeAll={() => router.push("/scores")}
              onUserPress={(userId) => router.push(`/(tabs)/profile/user/${userId}`)}
            />
          </View>
        )}

        {/* Scores Card */}
        <View className="px-4 mb-6">
          <TouchableOpacity
            onPress={() => router.push("/scores")}
            className="rounded-xl p-4 flex-row items-center justify-between"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            activeOpacity={0.7}
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                style={{ backgroundColor: "#10B98120" }}
              >
                <Ionicons name="podium" size={24} color="#10B981" />
              </View>
              <View>
                <Text className="font-semibold text-base" style={{ color: colors.text }}>
                  {activeWeekend ? `Scorelijst ${activeWeekend.name}` : "Scorelijst"}
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Bekijk de volledige ranglijst
                </Text>
              </View>
            </View>
            <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
          </TouchableOpacity>
        </View>

        {/* Loading State */}
        {isLoading && (
          <View className="py-8 items-center">
            <ActivityIndicator color={colors.primary} />
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * Weekend Game Card (Horizontal scroll)
 */
interface WeekendGameCardProps {
  game: GameWithConfig;
  colors: ReturnType<typeof useThemeColors>;
  onPress: () => void;
}

function WeekendGameCard({ game, colors, onPress }: WeekendGameCardProps) {
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="w-36 rounded-2xl p-4 mr-3"
      style={{
        backgroundColor: `${game.color}15`,
        borderWidth: 2,
        borderColor: game.color,
      }}
    >
      <View
        className="w-10 h-10 rounded-xl items-center justify-center mb-3"
        style={{ backgroundColor: `${game.color}30` }}
      >
        <Ionicons name={game.icon as any} size={20} color={game.color} />
      </View>
      <Text className="font-semibold" style={{ color: colors.text }} numberOfLines={1}>
        {game.name}
      </Text>
      <View className="flex-row items-center mt-2">
        <Ionicons name="star" size={12} color="#F59E0B" />
        <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
          {game.effective_points_config.win ||
            game.effective_points_config.correct_answer ||
            "+"} pts
        </Text>
      </View>
    </TouchableOpacity>
  );
}

/**
 * Free Play Game Card (Grid)
 */
interface FreePlayGameCardProps {
  game: GameWithConfig;
  colors: ReturnType<typeof useThemeColors>;
  onPress: () => void;
  isLeftColumn?: boolean;
}

function FreePlayGameCard({ game, colors, onPress, isLeftColumn = false }: FreePlayGameCardProps) {
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className={`w-[48%] rounded-2xl p-4 mb-3 ${isLeftColumn ? "mr-[4%]" : ""}`}
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
    >
      <View
        className="w-10 h-10 rounded-xl items-center justify-center mb-3"
        style={{ backgroundColor: `${game.color}20` }}
      >
        <Ionicons name={game.icon as any} size={20} color={game.color} />
      </View>
      <Text className="font-semibold" style={{ color: colors.text }}>
        {game.name}
      </Text>
      <Text className="text-xs mt-1" style={{ color: colors.textSecondary }} numberOfLines={1}>
        {game.description}
      </Text>
    </TouchableOpacity>
  );
}

/**
 * Urgent Challenge Card (Horizontal scroll)
 */
interface UrgentChallengeCardProps {
  challenge: ChallengeWithStatus;
  colors: ReturnType<typeof useThemeColors>;
  onPress: () => void;
  formatDeadline: (date: string) => string;
}

function UrgentChallengeCard({
  challenge,
  colors,
  onPress,
  formatDeadline,
}: UrgentChallengeCardProps) {
  const challengeColor = CHALLENGE_COLORS[challenge.challenge_type] || colors.primary;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="w-44 rounded-2xl p-4 mr-3"
      style={{
        backgroundColor: colors.error + "15",
        borderWidth: 1,
        borderColor: colors.error + "30",
      }}
    >
      <View
        className="absolute top-2 right-2 px-2 py-0.5 rounded-full"
        style={{ backgroundColor: colors.error }}
      >
        <Text className="text-xs font-bold text-white">
          {challenge.available_until ? formatDeadline(challenge.available_until) : ""}
        </Text>
      </View>

      <View
        className="w-10 h-10 rounded-xl items-center justify-center mb-3"
        style={{ backgroundColor: challengeColor + "30" }}
      >
        <Ionicons
          name={CHALLENGE_ICONS[challenge.challenge_type] as any}
          size={20}
          color={challengeColor}
        />
      </View>

      <Text className="font-semibold" style={{ color: colors.text }} numberOfLines={2}>
        {challenge.title}
      </Text>

      {challenge.has_points && (
        <View className="flex-row items-center mt-2">
          <Ionicons name="star" size={14} color="#F59E0B" />
          <Text className="text-sm ml-1 font-medium" style={{ color: "#F59E0B" }}>
            {challenge.points} pts
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

/**
 * Challenge List Card
 */
interface ChallengeListCardProps {
  challenge: ChallengeWithStatus;
  colors: ReturnType<typeof useThemeColors>;
  isDark: boolean;
  onPress: () => void;
}

function ChallengeListCard({
  challenge,
  colors,
  isDark,
  onPress,
}: ChallengeListCardProps) {
  const challengeColor = CHALLENGE_COLORS[challenge.challenge_type] || colors.primary;
  const isPending = challenge.completion?.status === "pending";

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="rounded-xl p-4 mb-3 flex-row items-center"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: isPending ? "#F59E0B" : colors.border,
      }}
    >
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: challengeColor + "20" }}
      >
        <Ionicons
          name={CHALLENGE_ICONS[challenge.challenge_type] as any}
          size={24}
          color={challengeColor}
        />
      </View>

      <View className="flex-1 ml-3">
        <Text className="font-semibold" style={{ color: colors.text }}>
          {challenge.title}
        </Text>
        <View className="flex-row items-center mt-1 flex-wrap">
          {challenge.target_type === "team" && (
            <View className="flex-row items-center mr-2">
              <Ionicons name="people" size={10} color={colors.textSecondary} />
              <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                Team
              </Text>
            </View>
          )}
          {isPending && (
            <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: "#F59E0B20" }}>
              <Text className="text-xs" style={{ color: "#F59E0B" }}>
                In afwachting
              </Text>
            </View>
          )}
        </View>
      </View>

      <View className="items-end">
        {challenge.has_points && (
          <View className="flex-row items-center">
            <Ionicons name="star" size={14} color="#F59E0B" />
            <Text className="ml-1 font-bold" style={{ color: "#F59E0B" }}>
              {challenge.points}
            </Text>
          </View>
        )}
        <Ionicons
          name="chevron-forward"
          size={18}
          color={colors.textTertiary}
          style={{ marginTop: 4 }}
        />
      </View>
    </TouchableOpacity>
  );
}
